import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

// BPO RH — Resumo por Centro de Custo (21/10/2026): a operadora importa o "Holerite em
// Excel" do Domínio, o Saber monta a planilha de líquidos por colaborador e centro de custo
// no modelo do cliente (Rubeus) e manda por e-mail com prévia. Vínculo vem da Convenia
// (PARTNER x SÓCIO a operadora escolhe e fica gravado); líquido de sócio é informado aqui.
// Backend: core/views_liquidos_cc.py
const API = process.env.REACT_APP_API_BASE;
const URL_LIQ = `${API}/api/bporh/liquidos-cc/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (v) => (v == null || v === '' ? '' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const erroMsg = (e, padrao) => e.response?.data?.mensagem || e.response?.data?.erro || padrao;
// a folha fecha no mês seguinte: a competência padrão é o mês anterior
const mesAnterior = () => {
  const d = new Date();
  d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const TEXTOS = [['lancado', 'Lançado'], ['gratificacao', 'Gratificação'], ['plano_saude', 'Plano de Saúde'], ['alteracoes', 'Alterações salariais']];

export default function BpoRhLiquidosCC() {
  const [params, setParams] = useSearchParams();
  const clienteId = params.get('cliente') || '';
  const mes = params.get('mes') || mesAnterior();
  const [clientes, setClientes] = useState(null);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado] = useState('');
  const [busca, setBusca] = useState('');
  const [filtroCC, setFiltroCC] = useState('');
  const [soPendentes, setSoPendentes] = useState(false);
  const [email, setEmail] = useState(null);
  const arquivoRef = useRef(null);
  const reqRef = useRef(0);

  const trocar = (novo) => {
    const p = new URLSearchParams(params);
    Object.entries(novo).forEach(([k, val]) => (val ? p.set(k, val) : p.delete(k)));
    setParams(p);
  };

  const carregarClientes = useCallback(() => {
    axios.get(URL_LIQ, { params: { mes }, headers: headers() })
      .then((r) => setClientes(r.data.clientes || []))
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível carregar as empresas')}`));
  }, [mes]);

  const carregar = useCallback(() => {
    if (!clienteId) { setDados(null); return; }
    const id = ++reqRef.current;
    setCarregando(true);
    axios.get(URL_LIQ, { params: { cliente_id: clienteId, mes, _: Date.now() }, headers: headers() })
      .then((r) => { if (id === reqRef.current) setDados(r.data); })
      .catch((e) => { if (id === reqRef.current) { setDados(null); notificar(`❌ ${erroMsg(e, 'Não foi possível carregar')}`); } })
      .finally(() => { if (id === reqRef.current) setCarregando(false); });
  }, [clienteId, mes]);

  useEffect(() => { carregarClientes(); }, [carregarClientes]);
  useEffect(() => { carregar(); }, [carregar]);

  const importar = (ev) => {
    const f = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!f) return;
    const fd = new FormData();
    fd.append('arquivo', f);
    fd.append('cliente_id', clienteId);
    fd.append('mes', mes);
    setOcupado('importar');
    axios.post(URL_LIQ, fd, { headers: headers(), timeout: 240000 })
      .then((r) => {
        notificar(`✅ ${r.data.mensagem}`);
        if (r.data.mes && r.data.mes !== mes) trocar({ mes: r.data.mes }); else setDados(r.data);
        carregarClientes();
      })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível ler o relatório')}`))
      .finally(() => setOcupado(''));
  };

  const acao = (corpo, chave, avisar = true) => {
    setOcupado(chave);
    return axios.post(URL_LIQ, { cliente_id: Number(clienteId), mes, ...corpo }, { headers: headers() })
      .then((r) => { setDados((d) => ({ ...d, ...r.data })); if (avisar && r.data.mensagem) notificar(`✅ ${r.data.mensagem}`); })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível salvar')}`))
      .finally(() => setOcupado(''));
  };
  const ajustar = (p, campo, valor) => acao({ acao: 'ajuste', chave: p.chave, campo, valor }, `aj-${p.chave}`, false);
  const salvarVinculo = (p, vinculo) => acao({ acao: 'vinculo', chave: p.chave, vinculo }, `vi-${p.chave}`);

  const exportar = () => {
    setOcupado('exportar');
    axios.get(URL_LIQ, { params: { exportar: 1, cliente_id: clienteId, mes, _: Date.now() }, headers: headers(), responseType: 'blob' })
      .then((r) => {
        const cd = r.headers['content-disposition'] || '';
        const utf = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const simples = cd.match(/filename="?([^";]+)"?/i);
        const nome = utf ? decodeURIComponent(utf[1]) : (simples ? simples[1] : `Liquidos_${mes}.xlsx`);
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = nome;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
      })
      .catch(async (e) => {
        let msg = 'Não foi possível gerar a planilha';
        try { msg = JSON.parse(await e.response.data.text()).mensagem || msg; } catch (_) { /* mantém */ }
        notificar(`❌ ${msg}`);
      })
      .finally(() => setOcupado(''));
  };

  const verPrevia = (e) => {
    setEmail((x) => ({ ...(x || e), carregando: true }));
    axios.post(URL_LIQ, { acao: 'email', enviar: false, cliente_id: Number(clienteId), mes,
      destinatarios: e.para, copia: e.copia, operador_id: e.operador_id || undefined }, { headers: headers() })
      .then((r) => setEmail((x) => ({ ...(x || e), previa: r.data.previa, carregando: false })))
      .catch((er) => { setEmail((x) => (x ? { ...x, carregando: false } : x)); notificar(`❌ ${erroMsg(er, 'Não foi possível montar o e-mail')}`); });
  };
  const abrirEmail = () => {
    const e = dados?.envio || {};
    const novo = { para: e.para || '', copia: e.copia || '', operador_id: String(e.operador_sugerido_id || ''), previa: null, carregando: false };
    setEmail(novo);
    verPrevia(novo);
  };
  const enviarEmail = () => {
    setOcupado('email');
    axios.post(URL_LIQ, { acao: 'email', enviar: true, cliente_id: Number(clienteId), mes,
      destinatarios: email.para, copia: email.copia, operador_id: email.operador_id || undefined,
      assunto: email.previa?.assunto, corpo_html: email.previa?.corpo_html }, { headers: headers(), timeout: 120000 })
      .then((r) => { notificar(`✅ ${r.data.mensagem}`); setDados((d) => ({ ...d, ...r.data })); setEmail(null); carregarClientes(); })
      .catch((er) => notificar(`❌ ${erroMsg(er, 'Não foi possível enviar')}`))
      .finally(() => setOcupado(''));
  };
  const salvarEmails = () => {
    setOcupado('salvar-email');
    axios.post(URL_LIQ, { acao: 'salvar_emails', cliente_id: Number(clienteId), mes, destinatarios: email.para, copia: email.copia },
      { headers: headers() })
      .then((r) => { notificar(`✅ ${r.data.mensagem}`); setDados((d) => ({ ...d, envio: r.data.envio })); })
      .catch((er) => notificar(`❌ ${erroMsg(er, 'Não foi possível salvar os e-mails')}`))
      .finally(() => setOcupado(''));
  };
  const mudarEmail = (campo, valor) => setEmail((x) => ({ ...x, [campo]: valor, previa: null }));

  const pessoas = useMemo(() => dados?.pessoas || [], [dados]);
  const resumo = dados?.resumo || {};
  const centros = dados?.centros || [];
  const visiveis = useMemo(() => {
    const b = busca.trim().toLowerCase();
    return pessoas.filter((p) => (!b || p.nome.toLowerCase().includes(b) || (p.cargo || '').toLowerCase().includes(b))
      && (!filtroCC || (p.centro_custo || '(sem centro de custo)') === filtroCC)
      && (!soPendentes || p.alerta));
  }, [pessoas, busca, filtroCC, soPendentes]);

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-sitemap header-icon me-2" aria-hidden="true"></i>
          <h2 className="titulo-pagina m-0">BPO RH — Resumo por Centro de Custo</h2></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 230 }} value={clienteId} onChange={(e) => trocar({ cliente: e.target.value })} aria-label="Empresa">
            <option value="">{clientes ? 'Escolha a empresa…' : 'Carregando…'}</option>
            {(clientes || []).map((c) => (
              <option key={c.id} value={c.id}>{c.cliente}{c.enviado ? ' · enviado' : c.importado ? ' · importado' : ''}</option>
            ))}
          </select>
          <input type="month" className="form-control form-control-sm" style={{ width: 150 }} value={mes} onChange={(e) => e.target.value && trocar({ mes: e.target.value })} aria-label="Competência" />
          <button type="button" className="gr-btn" onClick={carregar} disabled={carregando || !clienteId}><i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar</button>
        </div>
      </div>

      {!clienteId && <div className="text-muted small py-4 text-center">Escolha a empresa para começar.</div>}

      {clienteId && dados && (<>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <input type="file" accept=".xlsx,.xlsm" ref={arquivoRef} onChange={importar} style={{ display: 'none' }} />
          <button type="button" className="btn btn-sm btn-primary" onClick={() => arquivoRef.current?.click()} disabled={!!ocupado}>
            {ocupado === 'importar'
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Lendo o relatório…</>
              : <><i className="fas fa-file-excel me-2"></i>Importar Holerite em Excel (Domínio)</>}
          </button>
          <span className="small text-muted">
            {dados.importado
              ? <>Importado em {dados.importado.em}{dados.importado.por ? ` por ${dados.importado.por}` : ''} ({dados.importado.arquivo}). Reimportar substitui o mês — os ajustes ficam.</>
              : 'Relatório "Holerite em Excel" do Domínio: uma linha por colaborador com o LÍQUIDO e o centro de custo.'}
          </span>
          {dados.importado && pessoas.length > 0 && !pessoas.some((x) => 'gratificacao_folha' in x) && (
            <span className="small text-warning-emphasis">
              <i className="fas fa-triangle-exclamation me-1"></i>Reimporte o relatório para trazer Gratificação e Plano de Saúde da folha.
            </span>
          )}
        </div>

        {!pessoas.length && (
          <div className="text-muted small py-4 text-center">Nenhum relatório importado em {dados.mes_rotulo}.</div>
        )}

        {pessoas.length > 0 && (<>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {centros.map((c) => (
              <button type="button" key={c.centro_custo} onClick={() => setFiltroCC(filtroCC === c.centro_custo ? '' : c.centro_custo)}
                className="border rounded px-3 py-2 text-start small"
                style={{ background: filtroCC === c.centro_custo ? '#e8f7fb' : '#f8f9fa', borderColor: filtroCC === c.centro_custo ? '#00b5cc' : undefined }}
                title="Filtrar por este centro de custo">
                <div className="fw-bold" style={{ color: '#00205b' }}>{c.centro_custo}</div>
                <div>{c.qtd} pessoa(s) · <b>{brl(c.total)}</b></div>
              </button>
            ))}
          </div>

          <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
            <span><b>{resumo.qtd}</b> colaborador(es) · total <b>{brl(resumo.total)}</b></span>
            {resumo.alertas > 0 && (
              <button type="button" className={`btn btn-sm ${soPendentes ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => setSoPendentes((x) => !x)}>
                <i className="fas fa-triangle-exclamation me-1"></i>{resumo.alertas} pendência(s)
              </button>
            )}
            <input type="search" className="form-control form-control-sm" style={{ width: 220 }} placeholder="Buscar colaborador ou cargo"
              value={busca} onChange={(e) => setBusca(e.target.value)} />
            <div className="ms-auto d-flex gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={exportar} disabled={!!ocupado} title="Baixar a planilha (para conferir — o envio é pelo e-mail)">
                <i className={`fas ${ocupado === 'exportar' ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
              </button>
              <button type="button" className="btn btn-sm btn-primary" onClick={abrirEmail} disabled={!!ocupado}
                title={resumo.alertas ? `Há ${resumo.alertas} pendência(s) — confira antes de enviar` : 'Mandar a planilha ao cliente, já anexada'}>
                <i className="fas fa-paper-plane me-1"></i>Enviar planilha por e-mail
              </button>
            </div>
          </div>
          {resumo.alertas > 0 && (
            <div className="alert alert-warning small py-2">
              Sócio/partner: o Domínio traz só o pró-labore — digite o <b>líquido total</b> (com a distribuição de lucros) na coluna Líquido.
              Sem vínculo: escolha na coluna Vínculo (a escolha vale para os próximos meses).
            </div>
          )}

          <div className="table-responsive" style={{ maxHeight: '65vh' }}>
            <table className="table table-sm table-hover align-middle" style={{ fontSize: '.82rem' }}>
              <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th>Colaborador</th><th>Centro de custo</th><th>Vínculo</th>
                  <th className="text-center">Empréstimo?</th><th className="text-center">Férias?</th>
                  <th className="text-end">Líquido Domínio</th><th className="text-end" style={{ minWidth: 120 }}>Líquido</th>
                  {TEXTOS.map(([k, r]) => <th key={k} style={{ minWidth: 110 }}>{r}</th>)}
                </tr>
              </thead>
              <tbody>
                {visiveis.map((p) => (
                  <tr key={p.chave} style={p.alerta ? { background: '#fff8e6' } : undefined}>
                    <td>
                      <div className="fw-semibold">{p.nome}</div>
                      <div className="small text-muted">{p.cargo || '—'}</div>
                      {p.alerta && <div className="small text-warning-emphasis"><i className="fas fa-triangle-exclamation me-1"></i>{p.alerta}</div>}
                    </td>
                    <td className="small">{p.centro_custo || <span className="text-danger">sem centro de custo</span>}</td>
                    <td>
                      <select className={`form-select form-select-sm ${p.vinculo ? '' : 'border-warning'}`} style={{ width: 125 }}
                        value={p.vinculo || ''} disabled={!!ocupado} onChange={(e) => salvarVinculo(p, e.target.value)}
                        title={p.vinculo_origem === 'operadora' ? 'Escolhido pela operadora' : p.vinculo_origem === 'convenia' ? 'Da Convenia' : 'Sem vínculo'}>
                        <option value="">—</option>
                        {(dados.vinculos || []).map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </td>
                    <td className="text-center">{p.emprestimo ? <span className="badge bg-light text-dark border" title={brl(p.emprestimo_valor)}>SIM</span> : ''}</td>
                    <td className="text-center">{p.ferias ? <span className="badge bg-light text-dark border">SIM</span> : ''}</td>
                    <td className="text-end text-muted">{brl(p.liquido_dominio)}</td>
                    <td className="text-end">
                      <input type="text" className={`form-control form-control-sm text-end ${p.liquido_editado ? 'fw-bold' : ''}`}
                        key={`liq-${p.chave}-${p.liquido}`} defaultValue={num(p.liquido)}
                        style={p.liquido_editado ? { background: '#e8f7fb' } : undefined}
                        title={p.liquido_editado ? 'Editado — apague para voltar ao do Domínio' : 'Do Domínio — edite se precisar (ex.: sócio)'}
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val === num(p.liquido) && p.liquido_editado) return;
                          if (val === num(p.liquido_dominio) && !p.liquido_editado) return;
                          ajustar(p, 'liquido', val === num(p.liquido_dominio) ? '' : val);
                        }} />
                    </td>
                    {TEXTOS.map(([k]) => (
                      <td key={k}>
                        <input type="text" className="form-control form-control-sm" key={`${k}-${p.chave}-${p[k]}`} defaultValue={p[k] || ''}
                          onBlur={(e) => { if (e.target.value.trim() !== (p[k] || '')) ajustar(p, k, e.target.value.trim()); }} />
                        {p.origens?.[k] && p.origens[k] !== 'manual' && (
                          <div className="small text-muted" style={{ fontSize: '.7rem' }}
                            title={p.origens[k] === 'folha' ? 'Veio do Holerite do Domínio — edite para trocar' : 'Veio do histórico salarial da Convenia — edite para trocar'}>
                            <i className={`fas ${p.origens[k] === 'folha' ? 'fa-file-invoice-dollar' : 'fa-plug'} me-1`}></i>
                            {p.origens[k] === 'folha' ? 'da folha' : 'da Convenia'}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={6} className="text-end">Total{visiveis.length !== pessoas.length ? ' (todos)' : ''}</td>
                  <td className="text-end">{brl(resumo.total)}</td>
                  <td colSpan={TEXTOS.length}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          {(dados.excluidos || []).length > 0 && (
            <div className="small text-muted mt-2">
              <i className="fas fa-user-minus me-1"></i>Fora da planilha (rescisão no mês, líquido pago na rescisão):{' '}
              {dados.excluidos.map((x) => x.nome).join(', ')}
            </div>
          )}
          {(dados.envio?.historico || []).length > 0 && (
            <div className="small text-muted mt-1">
              <i className="fas fa-envelope-circle-check me-1 text-success"></i>
              Planilha enviada: {dados.envio.historico.map((h) => `${h.em} para ${h.para}${h.por ? ` (${h.por})` : ''}`).join(' · ')}
            </div>
          )}
        </>)}
      </>)}

      {email && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-paper-plane me-2"></i>Enviar líquidos por centro de custo
                  </h5>
                  <small style={{ color: '#6c757d' }}>{dados?.cliente} · {dados?.mes_rotulo} · {resumo.qtd} colaborador(es) · {brl(resumo.total)}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setEmail(null)} disabled={ocupado === 'email'}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Para</label>
                    <input type="text" className="form-control form-control-sm" placeholder="rh@cliente.com.br"
                      value={email.para} onChange={(e) => mudarEmail('para', e.target.value)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Cópia (opcional)</label>
                    <input type="text" className="form-control form-control-sm" value={email.copia} onChange={(e) => mudarEmail('copia', e.target.value)} />
                  </div>
                </div>
                <div className="d-flex align-items-end gap-2 mt-2 flex-wrap">
                  <div>
                    <label className="form-label small fw-semibold">Enviar como</label>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={email.operador_id}
                      onChange={(e) => mudarEmail('operador_id', e.target.value)}>
                      <option value="">— escolha —</option>
                      {(dados?.envio?.operadores || []).map((o) => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{o.pronto ? '' : ' (sem senha de app)'}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={salvarEmails}
                    disabled={!!ocupado || !email.para.trim()} title="Guardar Para e Cópia para os próximos envios desta empresa">
                    <i className={`fas ${ocupado === 'salvar-email' ? 'fa-spinner fa-spin' : 'fa-save'} me-1`}></i>Salvar e-mails
                  </button>
                  {!email.previa && (
                    <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => verPrevia(email)} disabled={email.carregando}>
                      <i className={`fas ${email.carregando ? 'fa-spinner fa-spin' : 'fa-eye'} me-1`}></i>Atualizar prévia
                    </button>
                  )}
                </div>
                {email.carregando && !email.previa && <div className="small text-muted mt-3"><i className="fas fa-spinner fa-spin me-1"></i>Montando o e-mail…</div>}
                {email.previa && (<>
                  <div className="small mt-3 mb-2">
                    <div><b>Assunto:</b> {email.previa.assunto}</div>
                    <div><b>De:</b> {email.previa.de}</div>
                    <div><b>Para:</b> {email.previa.para || <span className="text-danger">informe o destinatário</span>}</div>
                    {email.previa.copia && <div><b>Cópia:</b> {email.previa.copia}</div>}
                    <div><b>Anexo:</b> <i className="fas fa-file-excel text-success me-1"></i>{email.previa.anexo}</div>
                  </div>
                  {email.previa.pendencias > 0 && (
                    <div className="alert alert-warning small py-2">
                      <i className="fas fa-triangle-exclamation me-1"></i>
                      Ainda há <b>{email.previa.pendencias}</b> pendência(s) na planilha (líquido de sócio sem ajuste ou sem vínculo).
                      Se o líquido do Domínio já está certo, pode enviar assim mesmo.
                    </div>
                  )}
                  <div className="border rounded" style={{ maxHeight: 340, overflow: 'auto' }}>
                    <iframe title="Prévia do e-mail" srcDoc={email.previa.corpo_previa || email.previa.corpo_html}
                      sandbox="" style={{ width: '100%', height: 340, border: 0 }} />
                  </div>
                </>)}
              </div>
              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted">A planilha vai anexada — não precisa baixar nada.</span>
                <button className="btn btn-secondary" onClick={() => setEmail(null)} disabled={ocupado === 'email'}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
                <button className="btn btn-success" onClick={enviarEmail}
                  disabled={ocupado === 'email' || !email.previa || !email.para.trim() || !email.operador_id}
                  title={!email.previa ? 'Atualize a prévia antes de enviar' : undefined}>
                  <i className={`fas ${ocupado === 'email' ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                  {ocupado === 'email' ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div></div>
  );
}
